import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

const MERGE_DATA_LOSS_MESSAGE = 'Merging cells will only preserve the top-leftmost value. Merge anyway?';

// Cap the number of table names listed in the subordinate-reference warning; the rest are summarized as
// "(N more)" so the dialog stays readable for entries with many referencing tables.
const MAX_REFERENCING_TABLES_SHOWN = 10;

interface MergeRangeConfirmationModalProps {
  requiresConfirmation: boolean;
  tablesReferencingMergedChildCells: Array<string>;
}

/**
 * Builds the body of the "Merge Selection" confirmation dialog. Includes the data-loss warning when the
 * merge would discard cell values, and a subordinate-reference warning listing (in bold, each on its own
 * line) every table that has formulas referencing child cells of the region being merged. When more than
 * {@link MAX_REFERENCING_TABLES_SHOWN} tables reference the range, the overflow is summarized as "(N more)".
 */
export function MergeRangeConfirmationModal({
  requiresConfirmation,
  tablesReferencingMergedChildCells,
}: MergeRangeConfirmationModalProps): JSX.Element {
  const hasReferenceWarning = tablesReferencingMergedChildCells.length > 0;
  const shownTables = tablesReferencingMergedChildCells.slice(0, MAX_REFERENCING_TABLES_SHOWN);
  const remainingCount = tablesReferencingMergedChildCells.length - shownTables.length;

  return (
    <div {...getTestProps({component: 'MergeRangeConfirmationModal', element: 'root'})}>
      {requiresConfirmation && (
        <>
          <span {...getTestProps({component: 'MergeRangeConfirmationModal', element: 'dataLossWarning'})}>
            {MERGE_DATA_LOSS_MESSAGE}
          </span>
          {hasReferenceWarning && (
            <>
              <br />
              <br />
            </>
          )}
        </>
      )}
      {hasReferenceWarning && (
        <>
          <span {...getTestProps({component: 'MergeRangeConfirmationModal', element: 'referenceWarning'})}>
            There are formulas that reference child cells in the selected merge range. These references may no
            longer be correct.
          </span>
          <br />
          <br />
          Tables:
          <ul>
            {shownTables.map((tableName) => (
              <li key={tableName}>
                <strong>{tableName}</strong>
              </li>
            ))}
            {remainingCount > 0 && <li>({remainingCount} more)</li>}
          </ul>
        </>
      )}
    </div>
  );
}
